import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Send, Mail, Search, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Tolerate both array and { data: [...] } / { users: [...] } responses
const _extractList = (resp, ...keys) => {
  if (Array.isArray(resp)) return resp;
  if (!resp || typeof resp !== 'object') return [];
  if (Array.isArray(resp.data)) return resp.data;
  for (const k of keys) {
    if (Array.isArray(resp[k])) return resp[k];
  }
  return [];
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export default function AdminInboxSection({ getAuthHeader }) {
  const [tab, setTab] = useState('send');
  const [users, setUsers] = useState([]);
  const [sent, setSent] = useState([]);
  const [loadingSent, setLoadingSent] = useState(false);

  // Send form state
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipientType, setRecipientType] = useState('all');
  const [recipientUserId, setRecipientUserId] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [sending, setSending] = useState(false);

  const filteredUsers = useMemo(() => {
    const q = userFilter.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  }, [users, userFilter]);

  const loadUsers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/users-with-plans`, { headers: getAuthHeader() });
      setUsers(_extractList(r.data, 'users', 'items'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nutzer konnten nicht geladen werden');
    }
  }, [getAuthHeader]);

  const loadSent = useCallback(async () => {
    setLoadingSent(true);
    try {
      const r = await axios.get(`${API}/admin/inbox/sent`, { headers: getAuthHeader() });
      setSent(_extractList(r.data, 'messages', 'items'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gesendete Nachrichten konnten nicht geladen werden');
    } finally {
      setLoadingSent(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { if (tab === 'sent') loadSent(); }, [tab, loadSent]);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Betreff und Nachricht sind erforderlich'); return; }
    if (recipientType === 'single' && !recipientUserId) { toast.error('Bitte einen Empfänger auswählen'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/inbox/send`, {
        subject: subject.trim(),
        body: body.trim(),
        recipient_type: recipientType,
        recipient_user_id: recipientType === 'single' ? recipientUserId : null,
      }, { headers: getAuthHeader() });
      toast.success(`Gesendet an ${r.data?.sent_to || 0} Empfänger`);
      setSubject(''); setBody(''); setRecipientUserId(''); setRecipientType('all');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-inbox-section">
      {/* Sub-tabs */}
      <div className="flex gap-1 bg-[#0A1628] rounded-lg p-1 w-fit">
        <button
          onClick={() => setTab('send')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'send' ? 'bg-[#1A2B42] text-white' : 'text-[#5A6C7D] hover:text-white'}`}
          data-testid="inbox-tab-send"
        >
          <Send className="w-3.5 h-3.5 inline mr-1.5" />
          Senden
        </button>
        <button
          onClick={() => setTab('sent')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'sent' ? 'bg-[#1A2B42] text-white' : 'text-[#5A6C7D] hover:text-white'}`}
          data-testid="inbox-tab-sent"
        >
          <Mail className="w-3.5 h-3.5 inline mr-1.5" />
          Gesendet
        </button>
      </div>

      {tab === 'send' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-4" data-testid="inbox-send-form">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Betreff</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                   className="text-gray-900 placeholder:text-gray-400"
                   placeholder="Z.B. Neue Features verfügbar" data-testid="inbox-subject-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Nachricht</label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)}
                      rows={6} placeholder="Deine Nachricht an die Nutzer …"
                      className="text-gray-900 placeholder:text-gray-400"
                      data-testid="inbox-body-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Empfänger</label>
            <div className="flex gap-4 mb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="recipientType" value="all" checked={recipientType === 'all'}
                       onChange={() => setRecipientType('all')} data-testid="inbox-recipient-all" />
                <span className="text-sm text-gray-700">Alle Nutzer ({users.length})</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="recipientType" value="single" checked={recipientType === 'single'}
                       onChange={() => setRecipientType('single')} data-testid="inbox-recipient-single" />
                <span className="text-sm text-gray-700">Einzelner Nutzer</span>
              </label>
            </div>
            {recipientType === 'single' && (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <Input
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    placeholder="Nutzer filtern (Name oder E-Mail)…"
                    className="pl-9 text-sm text-gray-900 placeholder:text-gray-400"
                    data-testid="inbox-user-filter"
                  />
                </div>
                {/* Custom click-list (replaces native <select> for cross-device reliability) */}
                <div
                  className="border border-gray-300 rounded-md max-h-72 overflow-y-auto bg-white"
                  data-testid="inbox-user-list"
                >
                  {filteredUsers.length === 0 ? (
                    <div className="px-3 py-4 text-sm text-gray-400 text-center">Keine Nutzer gefunden</div>
                  ) : (
                    filteredUsers.map(u => {
                      const selected = recipientUserId === u.id;
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => setRecipientUserId(u.id)}
                          className={`w-full text-left px-3 py-2.5 text-sm flex items-center justify-between gap-2 border-b border-gray-100 last:border-b-0 transition-colors ${selected ? 'bg-[#1e9ead]/10 text-[#0a1628]' : 'text-gray-900 hover:bg-gray-50'}`}
                          data-testid={`inbox-user-option-${u.id}`}
                        >
                          <span className="truncate">
                            <span className={selected ? 'font-medium' : ''}>{u.name || u.email}</span>{' '}
                            <span className="text-xs text-gray-500">({u.email})</span>
                          </span>
                          {selected && <CheckCircle2 className="w-4 h-4 text-[#1e9ead] flex-shrink-0" />}
                        </button>
                      );
                    })
                  )}
                </div>
                <p className="text-xs text-gray-500">{filteredUsers.length} von {users.length} Nutzern</p>
              </div>
            )}
          </div>
          <Button onClick={handleSend} disabled={sending}
                  className="bg-[#1e9ead] hover:bg-[#197b87] text-white"
                  data-testid="inbox-send-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Nachricht senden
          </Button>
          <p className="text-xs text-gray-500">
            Die Nachricht wird im In-App-Posteingang und per E-Mail (Resend) zugestellt.
          </p>
        </div>
      )}

      {tab === 'sent' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loadingSent ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1e9ead]" /></div>
          ) : sent.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-gray-500">Noch keine Nachrichten gesendet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Datum</th>
                    <th className="px-4 py-3 text-left font-medium">Betreff</th>
                    <th className="px-4 py-3 text-left font-medium">Empfänger</th>
                    <th className="px-4 py-3 text-left font-medium">Vorschau</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sent.map(m => (
                    <tr key={m._id} className="hover:bg-gray-50" data-testid={`sent-row-${m._id}`}>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatDate(m.created_at)}</td>
                      <td className="px-4 py-3 text-gray-900 font-medium">{m.subject}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {m.recipient_type === 'all' ? 'Alle Nutzer' : `Einzeln (${m.recipient_user_id?.slice(0, 8) || '—'}…)`}
                      </td>
                      <td className="px-4 py-3 text-gray-500 max-w-md truncate">{(m.body || '').slice(0, 60)}{(m.body || '').length > 60 ? '…' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
